/**
 * Flight cargo section of the search form: service level, temperature range,
 * commodity, loose packages or ULDs, batteries and packaging. Part of
 * LaneSearchForm; its values map to the neutral `air` block (airRequest.ts).
 * "Active container" is not offered yet. The packaging option is
 * Zoomlogi-only (it flags quotes over the stability budget) and is never sent.
 */
import React, { useMemo } from 'react';
import { Button, Checkbox, Form, Tooltip } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

import { CustomInput, CustomSelect } from '../../../components';
import {
  AirBatteries,
  AirPieceValues,
  AirServiceLevel,
  AirTempRange,
  MAX_ROWS,
  TEMP_SERVICE_LEVELS,
} from './airRequest';
import { integerRule, numberRule } from './formRules';
import { findPackagingOption, PACKAGING_OPTIONS } from './packagingOptions';
import styles from './styles.module.css';

/** IATA volumetric factor for the client-side chargeable-weight preview. */
const VOLUMETRIC_KG_PER_M3 = 167;

const SERVICE_LEVEL_OPTIONS: Array<{
  value: AirServiceLevel;
  label: string;
}> = [
  { value: 'GENERAL', label: 'General cargo' },
  {
    value: 'PASSIVE_TEMPERATURE_CONTROL',
    label: 'Passive temperature control',
  },
  { value: 'PHARMACEUTICALS', label: 'Pharmaceuticals' },
  { value: 'PERISHABLES', label: 'Perishables' },
  { value: 'SPECIAL_CARGO', label: 'Special cargo' },
  { value: 'OTHER', label: 'Other' },
];

const TEMP_RANGE_OPTIONS: Array<{ value: AirTempRange; label: string }> = [
  { value: 'COL', label: '2–8°C (COL)' },
  { value: 'CRT', label: '15–25°C (CRT)' },
  { value: 'ERT', label: '2–25°C (ERT)' },
  { value: 'FRO', label: '−20 to −10°C (FRO)' },
];

/** Documented goodsDescription → commodityName combinations. */
const COMMODITY_BY_SERVICE: Record<
  AirServiceLevel,
  Array<{ value: string; label: string }>
> = {
  GENERAL: [
    { value: 'MISCELLANEOUS', label: 'Miscellaneous' },
    { value: 'AOG', label: 'AOG (aircraft on ground)' },
    { value: 'LITHIUM_BATTERY_ION', label: 'Lithium batteries — ion' },
    { value: 'LITHIUM_BATTERY_METAL', label: 'Lithium batteries — metal' },
    {
      value: 'LITHIUM_BATTERY_ION_METAL',
      label: 'Lithium batteries — ion + metal',
    },
  ],
  PASSIVE_TEMPERATURE_CONTROL: [
    { value: 'PHARMACEUTICALS', label: 'Pharmaceuticals' },
    { value: 'MEDICINES', label: 'Medicines' },
    { value: 'VACCINES', label: 'Vaccines' },
    { value: 'MEDICAL_EQUIPMENTS', label: 'Medical equipment' },
    { value: 'MEDICAL_IMPLANTS', label: 'Medical implants' },
    { value: 'CHEMICALS', label: 'Chemicals' },
    { value: 'FOOD', label: 'Food' },
    { value: 'FISH_SEAFOOD', label: 'Fish / seafood' },
    { value: 'MEAT', label: 'Meat' },
    { value: 'FRUIT_VEGETABLES', label: 'Fruit / vegetables' },
    { value: 'FLOWERS_PLANTS', label: 'Flowers / plants' },
    { value: 'HIGH_TECH_MATERIALS', label: 'High-tech materials' },
    { value: 'AUTOMOBILES', label: 'Automobiles' },
  ],
  PHARMACEUTICALS: [
    { value: 'PHARMACEUTICALS', label: 'Pharmaceuticals' },
    { value: 'MEDICINES', label: 'Medicines' },
    { value: 'VACCINES', label: 'Vaccines' },
    { value: 'MEDICAL_EQUIPMENTS', label: 'Medical equipment' },
    { value: 'MEDICAL_IMPLANTS', label: 'Medical implants' },
  ],
  PERISHABLES: [
    { value: 'FOOD', label: 'Food' },
    { value: 'FISH_SEAFOOD', label: 'Fish / seafood' },
    { value: 'MEAT', label: 'Meat' },
    { value: 'FRUIT_VEGETABLES', label: 'Fruit / vegetables' },
    { value: 'FLOWERS_PLANTS', label: 'Flowers / plants' },
  ],
  SPECIAL_CARGO: [
    { value: 'AIRCRAFT_PARTS', label: 'Aircraft parts' },
    { value: 'AOG', label: 'AOG (aircraft on ground)' },
    { value: 'COURIER', label: 'Courier' },
    { value: 'COMAT', label: 'COMAT' },
    { value: 'DUAL_USE', label: 'Dual use' },
    { value: 'MILITARY_EQUIPMENT', label: 'Military equipment' },
    { value: 'MILITARY_AIRCRAFT_PARTS', label: 'Military aircraft parts' },
  ],
  OTHER: [
    { value: 'HIGH_TECH_MATERIALS', label: 'High-tech materials' },
    { value: 'CHEMICALS', label: 'Chemicals' },
    { value: 'AUTOMOBILES', label: 'Automobiles' },
    { value: 'LIVE_ANIMALS', label: 'Live animals' },
    { value: 'HUMAN_REMAINS', label: 'Human remains' },
    { value: 'VULNERABLE', label: 'Vulnerable / high value' },
  ],
};

/** Curated labels for common ULD type codes (all values are API enum codes). */
const ULD_TYPE_OPTIONS = [
  { value: 'PMC', label: 'PMC — pallet (96 × 125 in)' },
  { value: 'PAJ', label: 'PAJ — pallet (88 × 125 in)' },
  { value: 'PLA', label: 'PLA — half pallet (60 × 125 in)' },
  { value: 'PGA', label: 'PGA — 20 ft pallet' },
  { value: 'AKE', label: 'AKE — LD3 container' },
  { value: 'AKH', label: 'AKH — LD3-45 container' },
  { value: 'AMA', label: 'AMA — M1 container' },
  { value: 'AAP', label: 'AAP — LD9 container' },
  { value: 'ALF', label: 'ALF — LD6 container' },
  { value: 'AMF', label: 'AMF — M1H container' },
];

const BATTERY_OPTIONS: Array<{ value: AirBatteries; label: string }> = [
  { value: 'ELI', label: 'Lithium ion (ELI)' },
  { value: 'ELM', label: 'Lithium metal (ELM)' },
  { value: 'COMBINATION', label: 'Ion + metal (combination)' },
];

const AirCargoSection: React.FC<{ title?: string }> = ({ title = 'Cargo' }) => {
  const form = Form.useFormInstance();

  const serviceLevel =
    (Form.useWatch('serviceLevel', form) as AirServiceLevel | undefined) ||
    'GENERAL';
  const shipmentKind =
    (Form.useWatch('shipmentKind', form) as 'LOOSE' | 'ULD' | undefined) ||
    'LOOSE';
  const hasBatteries = Form.useWatch('hasBatteries', form) as
    boolean | undefined;
  const packagingOptionValue = Form.useWatch('packagingOption', form) as
    string | undefined;
  const selectedPackaging = findPackagingOption(packagingOptionValue);
  const watchedPieces = Form.useWatch('pieces', form) as
    AirPieceValues[] | undefined;

  const showTempRange = TEMP_SERVICE_LEVELS.includes(serviceLevel);
  const commodityOptions = COMMODITY_BY_SERVICE[serviceLevel] ?? [];
  const commodityRequired = serviceLevel === 'PHARMACEUTICALS';

  // Client-side chargeable preview for loose cargo (rates come from the API).
  const looseSummary = useMemo(() => {
    if (shipmentKind !== 'LOOSE' || !Array.isArray(watchedPieces)) return null;
    let actualWeight = 0;
    let volume = 0;
    let units = 0;
    for (const piece of watchedPieces) {
      const qty = Number(piece?.quantity) || 0;
      const weight = Number(piece?.weight) || 0;
      const l = Number(piece?.length) || 0;
      const w = Number(piece?.width) || 0;
      const h = Number(piece?.height) || 0;
      units += qty;
      actualWeight += piece?.weightBasis === 'PER_UNIT' ? qty * weight : weight;
      volume += (qty * (l * w * h)) / 1_000_000;
    }
    if (!units) return null;
    const volumetric = volume * VOLUMETRIC_KG_PER_M3;
    return {
      units,
      actualWeight,
      volume,
      volumetric,
      chargeable: Math.max(actualWeight, volumetric),
    };
  }, [shipmentKind, watchedPieces]);

  const weightBasisSelect = (fieldName: number): React.ReactNode => (
    <Form.Item
      label='Basis'
      name={[fieldName, 'weightBasis']}
      style={{ marginBottom: 12 }}
    >
      <CustomSelect
        width='100%'
        height='36px'
        options={[
          { value: 'TOTAL', label: 'Total' },
          { value: 'PER_UNIT', label: 'Per unit' },
        ]}
      />
    </Form.Item>
  );

  return (
    <>
      <div className={styles.sectionTitle}>{title}</div>
      <Form.Item
        label={
          <span>
            Service level{' '}
            <Tooltip title='"Active container" (RKN/RAP) is not supported yet — passive temperature control covers temp-range searches.'>
              <span className={styles.muted}>ⓘ</span>
            </Tooltip>
          </span>
        }
        name='serviceLevel'
      >
        <CustomSelect
          width='100%'
          height='36px'
          options={SERVICE_LEVEL_OPTIONS}
          onChange={() => {
            // Commodity options are constrained per service level.
            form.setFieldValue('commodityName', undefined);
          }}
        />
      </Form.Item>
      {showTempRange && (
        <Form.Item
          label='Temp range'
          name='tempRangeCode'
          rules={
            serviceLevel === 'PASSIVE_TEMPERATURE_CONTROL'
              ? [
                  {
                    required: true,
                    message:
                      'Most airlines require a temperature range for passive temperature control.',
                  },
                ]
              : []
          }
        >
          <CustomSelect
            width='100%'
            height='36px'
            placeholder='Select range'
            options={TEMP_RANGE_OPTIONS}
          />
        </Form.Item>
      )}
      <Form.Item
        label='Commodity'
        name='commodityName'
        rules={
          commodityRequired
            ? [
                {
                  required: true,
                  message: 'A commodity is required for pharmaceuticals.',
                },
              ]
            : []
        }
      >
        <CustomSelect
          width='100%'
          height='36px'
          placeholder={commodityRequired ? 'Select commodity' : 'Optional'}
          options={commodityOptions}
        />
      </Form.Item>
      <Form.Item label='Shipment type' name='shipmentKind'>
        <CustomSelect
          width='100%'
          height='36px'
          options={[
            { value: 'LOOSE', label: 'Loose' },
            { value: 'ULD', label: 'ULD' },
          ]}
          onChange={(value: 'LOOSE' | 'ULD') => {
            // cargo.one rejects ULD searches unless consolidated=true
            // ("ULDs can only be used with consolidated shipments").
            if (value === 'ULD') form.setFieldValue('consolidated', true);
          }}
        />
      </Form.Item>
      <div className={styles.checkboxStack}>
        <Form.Item name='screened' valuePropName='checked'>
          <Checkbox>Screened</Checkbox>
        </Form.Item>
        <Form.Item name='consolidated' valuePropName='checked'>
          <Checkbox disabled={shipmentKind === 'ULD'}>
            Consolidated{shipmentKind === 'ULD' ? ' (required for ULD)' : ''}
          </Checkbox>
        </Form.Item>
        <Form.Item name='knownShipper' valuePropName='checked'>
          <Checkbox>Known shipper (required for U.S. origins)</Checkbox>
        </Form.Item>
      </div>

      {shipmentKind === 'LOOSE' ? (
        <>
          <div className={styles.sectionTitleSub}>Packages</div>
          <Form.List name='pieces'>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <div key={field.key} className={styles.packageLine}>
                    {fields.length > 1 && (
                      <div className={styles.packageLineHeader}>
                        <span className={styles.muted}>
                          Package line {index + 1}
                        </span>
                        <Button
                          type='text'
                          size='small'
                          aria-label='Remove package line'
                          icon={<CloseOutlined />}
                          onClick={() => remove(field.name)}
                        />
                      </div>
                    )}
                    <Form.Item
                      label='Quantity'
                      name={[field.name, 'quantity']}
                      style={{ marginBottom: 12 }}
                      rules={integerRule('Whole number, min 1')}
                    >
                      <CustomInput placeholder='1' height='36px' />
                    </Form.Item>
                    <div
                      className={`${styles.cargoRow} ${styles.cargoRowDims}`}
                    >
                      {(['length', 'width', 'height'] as const).map((dim) => (
                        <Form.Item
                          key={dim}
                          label={`${dim.charAt(0).toUpperCase()}${dim.slice(1)} (cm)`}
                          name={[field.name, dim]}
                          style={{ marginBottom: 12 }}
                          rules={numberRule('Required')}
                        >
                          <CustomInput placeholder='cm' height='36px' />
                        </Form.Item>
                      ))}
                    </div>
                    <div
                      className={`${styles.cargoRow} ${styles.cargoRowWeightPieces}`}
                    >
                      <Form.Item
                        label='Weight (kg)'
                        name={[field.name, 'weight']}
                        style={{ marginBottom: 12 }}
                        rules={numberRule('Required')}
                      >
                        <CustomInput placeholder='kg' height='36px' />
                      </Form.Item>
                      {weightBasisSelect(field.name)}
                    </div>
                    <Form.Item
                      name={[field.name, 'stackable']}
                      valuePropName='checked'
                      style={{ marginBottom: 8 }}
                    >
                      <Checkbox>Stackable</Checkbox>
                    </Form.Item>
                  </div>
                ))}
                {fields.length < MAX_ROWS && (
                  <button
                    type='button'
                    className={styles.addLink}
                    onClick={() =>
                      add({
                        quantity: 1,
                        weightBasis: 'TOTAL',
                        stackable: true,
                      })
                    }
                  >
                    + Add package line
                  </button>
                )}
                {looseSummary && (
                  <div className={styles.summaryBox}>
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryLabel}>Actual weight</span>
                      <span>
                        {looseSummary.actualWeight.toLocaleString()} kg (
                        {looseSummary.units} pc)
                      </span>
                    </div>
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryLabel}>
                        Volumetric (167 kg/m³)
                      </span>
                      <span>
                        {looseSummary.volumetric.toFixed(1)} kg ·{' '}
                        {looseSummary.volume.toFixed(2)} m³
                      </span>
                    </div>
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryLabel}>
                        Chargeable weight (est.)
                      </span>
                      <span>{looseSummary.chargeable.toFixed(1)} kg</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </Form.List>
        </>
      ) : (
        <>
          <div className={styles.sectionTitleSub}>Units</div>
          <Form.List name='ulds'>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <div key={field.key} className={styles.packageLine}>
                    {fields.length > 1 && (
                      <div className={styles.packageLineHeader}>
                        <span className={styles.muted}>
                          ULD line {index + 1}
                        </span>
                        <Button
                          type='text'
                          size='small'
                          aria-label='Remove ULD line'
                          icon={<CloseOutlined />}
                          onClick={() => remove(field.name)}
                        />
                      </div>
                    )}
                    <div className={`${styles.cargoRow} ${styles.cargoRowUld}`}>
                      <Form.Item
                        label='ULD type'
                        name={[field.name, 'uldType']}
                        style={{ marginBottom: 12 }}
                        rules={[{ required: true, message: 'Pick a ULD type' }]}
                      >
                        <CustomSelect
                          placeholder='Type'
                          width='100%'
                          height='36px'
                          options={ULD_TYPE_OPTIONS}
                        />
                      </Form.Item>
                      <Form.Item
                        label='Quantity'
                        name={[field.name, 'quantity']}
                        style={{ marginBottom: 12 }}
                        rules={integerRule('Whole number, min 1')}
                      >
                        <CustomInput placeholder='1' height='36px' />
                      </Form.Item>
                    </div>
                    <div
                      className={`${styles.cargoRow} ${styles.cargoRowUldWeight}`}
                    >
                      <Form.Item
                        label='Gross weight (kg)'
                        name={[field.name, 'weight']}
                        style={{ marginBottom: 12 }}
                        rules={numberRule('Required')}
                      >
                        <CustomInput placeholder='kg' height='36px' />
                      </Form.Item>
                      {weightBasisSelect(field.name)}
                      <Form.Item
                        label='Volume (m³)'
                        name={[field.name, 'volume']}
                        style={{ marginBottom: 12 }}
                        rules={numberRule('Required')}
                      >
                        <CustomInput placeholder='total' height='36px' />
                      </Form.Item>
                    </div>
                  </div>
                ))}
                {fields.length < MAX_ROWS && (
                  <button
                    type='button'
                    className={styles.addLink}
                    onClick={() =>
                      add({ quantity: 1, weightBasis: 'PER_UNIT' })
                    }
                  >
                    + Add ULD line
                  </button>
                )}
              </>
            )}
          </Form.List>
        </>
      )}

      <div className={styles.checkboxStack}>
        <Form.Item name='hasBatteries' valuePropName='checked'>
          <Checkbox>Contains lithium batteries</Checkbox>
        </Form.Item>
      </div>
      {hasBatteries && (
        <Form.Item
          label='Battery type'
          name='containsBatteries'
          rules={[{ required: true, message: 'Pick the battery type.' }]}
        >
          <CustomSelect
            width='100%'
            height='36px'
            placeholder='Select type'
            options={BATTERY_OPTIONS}
          />
        </Form.Item>
      )}
      <div className={styles.sectionTitle}>Packaging</div>
      <div className={styles.packagingBlock}>
        <Form.Item label='Option (optional)' name='packagingOption'>
          <CustomSelect
            options={PACKAGING_OPTIONS}
            placeholder='Select packaging'
            allowClear
            height='36px'
          />
        </Form.Item>
        {selectedPackaging && (
          <div className={`${styles.summaryBox} ${styles.packagingSummary}`}>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Temperature range</span>
              <span className={styles.packagingSummaryValue}>
                {selectedPackaging.temperatureRange}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>
                Published stability/autonomy
              </span>
              <span className={styles.packagingSummaryValue}>
                {selectedPackaging.publishedStability}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Planning budget</span>
              <span className={styles.packagingSummaryValue}>
                {selectedPackaging.planningBudget}
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AirCargoSection;
